/**
 * Sharing API — scoped share links (P4.2) and the talent-discovery opt-in (P4.3).
 *
 * The share token is a bearer secret. It is sent ONLY in a POST body
 * (/share/view), never in an API path or query string, because request paths
 * are written to access logs. The web link carries it in the URL fragment
 * (/s#<token>), which the browser never sends to any server.
 */

import { api } from '../api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export type ShareItemKind = 'profile_header' | 'highlight' | 'credential' | 'authority_summary';

export interface ShareItemRef {
  kind: ShareItemKind;
  id?: string;
}

export type ShareLinkStatus = 'active' | 'expired' | 'revoked';

export interface ShareLinkSummary {
  id: string;
  items: ShareItemRef[];
  status: ShareLinkStatus;
  expiresAt: string | null;
  revokedAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
  createdAt: string;
}

export interface CreatedShareLink extends ShareLinkSummary {
  /** Returned exactly once; the server keeps only its hash. */
  token: string;
  /** /s#<token> */
  path: string;
}

export interface CreateShareLinkRequest {
  items: ShareItemRef[];
  expiresAt?: string | null;
}

export type SharedItem =
  | { kind: 'profile_header'; displayName: string | null; headline: string | null }
  | {
      kind: 'highlight';
      id: string;
      title: string;
      description: string | null;
      sourceType: string;
      scenarioKey: string | null;
      durationSeconds: number | null;
      transcriptExcerpt: string | null;
      /** Coaching result items: what the result was judged on. */
      result?: { rubric: { id: string; version: string; title: string | null } | null; evidenceClasses: string[]; overall: number | null };
    }
  | {
      kind: 'credential';
      id: string;
      domainLabel: string;
      category: string;
      authorityLevelAtIssue: string;
      issuedAt: string;
      expiresAt: string | null;
      verificationHash: string;
    }
  | { kind: 'authority_summary'; tier: string; score: number; totalSessions: number };

export interface SharedView {
  items: SharedItem[];
  expiresAt: string | null;
}

export interface DiscoveryPreference {
  optedIn: boolean;
  optedInAt: string | null;
  /** Separate choice: recruiters may send contact requests (needs discovery too). */
  recruiterContactAllowed: boolean;
  recruiterContactAllowedAt: string | null;
}

/** The token from a share URL's fragment (`#<token>`), or null if absent/malformed. */
export function readShareToken(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  return /^[A-Za-z0-9_-]{43}$/.test(raw) ? raw : null;
}

export const sharingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMyShareLinks: builder.query<ShareLinkSummary[], void>({
      query: () => '/me/share-links',
      transformResponse: (r: ApiResponse<ShareLinkSummary[]>) => r.data,
      providesTags: ['ShareLinks'],
    }),
    createShareLink: builder.mutation<CreatedShareLink, CreateShareLinkRequest>({
      query: (body) => ({ url: '/me/share-links', method: 'POST', body }),
      transformResponse: (r: ApiResponse<CreatedShareLink>) => r.data,
      invalidatesTags: ['ShareLinks'],
    }),
    revokeShareLink: builder.mutation<ShareLinkSummary, string>({
      query: (id) => ({ url: `/me/share-links/${id}/revoke`, method: 'POST' }),
      transformResponse: (r: ApiResponse<ShareLinkSummary>) => r.data,
      invalidatesTags: ['ShareLinks'],
    }),
    // Public. POST so the token travels in the body, never in a logged path.
    viewSharedItems: builder.query<SharedView, string>({
      query: (token) => ({ url: '/share/view', method: 'POST', body: { token } }),
      transformResponse: (r: ApiResponse<SharedView>) => r.data,
      keepUnusedDataFor: 0,
    }),
    getMyDiscovery: builder.query<DiscoveryPreference, void>({
      query: () => '/me/discovery',
      transformResponse: (r: ApiResponse<DiscoveryPreference>) => r.data,
      providesTags: ['Discovery'],
    }),
    setMyDiscovery: builder.mutation<DiscoveryPreference, { optedIn?: boolean; recruiterContactAllowed?: boolean }>({
      query: (body) => ({ url: '/me/discovery', method: 'PUT', body }),
      transformResponse: (r: ApiResponse<DiscoveryPreference>) => r.data,
      invalidatesTags: ['Discovery', 'Marketplace'],
    }),
  }),
});

export const {
  useGetMyShareLinksQuery,
  useCreateShareLinkMutation,
  useRevokeShareLinkMutation,
  useViewSharedItemsQuery,
  useGetMyDiscoveryQuery,
  useSetMyDiscoveryMutation,
} = sharingApi;
